import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiErrorResponse, ErrorHandlerV2Service } from '@gea/digital-ui-lib';
import { isEqual } from 'lodash';
import { catchError, defer, EMPTY, map, Observable, of, tap } from 'rxjs';

import { FaultRecommendationsApiService } from '@shared/services';
import {
  AnnunciationsMetaData,
  CreateRecommendationFromResolvedFaultParams,
  FaultCatalog,
  FaultRecommendationsStateModel,
  FetchFaultCatalogParams,
  FetchResolvedFaultsParams,
  ResolvedFaultDTO,
} from '@shared/models';
import { TroubleshootingStore } from '../troubleshooting';

const INITIAL_STATE: FaultRecommendationsStateModel = {};

const EMPTY_RESOLVED_FAULTS: ResolvedFaultDTO = { total: 0, data: [] };

@Injectable({ providedIn: 'root' })
export class FaultRecommendationsStore {
  private readonly apiService = inject(FaultRecommendationsApiService);
  private readonly troubleshootingStore = inject(TroubleshootingStore);
  private readonly errorHandler = inject(ErrorHandlerV2Service);

  private readonly state = signal<FaultRecommendationsStateModel>(INITIAL_STATE);

  readonly page = computed<number | undefined>(() => this.state().page);
  readonly pageSize = computed<number | undefined>(() => this.state().pageSize);
  readonly fault = computed<number | undefined>(() => this.state().fault);
  readonly withoutRecommendations = computed<boolean | undefined>(() => this.state().withoutRecommendations);
  readonly faultCatalog = computed<FaultCatalog | undefined>(() => this.state().faultCatalog, { equal: isEqual });
  readonly annunciationsMetadata = computed<AnnunciationsMetaData | undefined>(() => this.state().annunciationsMetadata, {
    equal: isEqual,
  });
  readonly resolvedFaults = computed<ResolvedFaultDTO | undefined>(() => this.state().resolvedFaults, { equal: isEqual });

  fetchFaultCatalog(params: FetchFaultCatalogParams): void {
    this.patch({ faultCatalog: undefined });

    this.apiService
      .fetchFaultCatalog(params)
      .pipe(
        catchError((error: ApiErrorResponse) => {
          this.reportError(error);
          return EMPTY;
        })
      )
      .subscribe((faultCatalog) => this.patch({ faultCatalog }));
  }

  fetchAnnunciationsMetadata(): void {
    this.patch({ annunciationsMetadata: undefined });

    this.apiService
      .fetchAnnunciationsMetadata()
      .pipe(
        catchError((error: ApiErrorResponse) => {
          this.reportError(error);
          return EMPTY;
        })
      )
      .subscribe((annunciationsMetadata) => this.patch({ annunciationsMetadata }));
  }

  fetchResolvedFaults(params: FetchResolvedFaultsParams): void {
    this.patch({ resolvedFaults: undefined });

    this.apiService
      .fetchResolvedFaults(params)
      .pipe(
        catchError((error: ApiErrorResponse) => {
          this.reportError(error);
          return of(EMPTY_RESOLVED_FAULTS);
        })
      )
      .subscribe((resolvedFaults) => this.patch({ resolvedFaults }));
  }

  /**
   * Creates a recommendation from a resolved fault. Emits once when the recommendation was saved.
   * The caller must subscribe to trigger the request.
   */
  createRecommendation({ resolvedFault, reviewStatus }: CreateRecommendationFromResolvedFaultParams): Observable<void> {
    return defer(() => this.apiService.createRecommendationFromResolvedFault({ resolvedFault, reviewStatus })).pipe(
      tap((newRecommendation) => {
        if (newRecommendation) {
          this.troubleshootingStore.fetchRecommendations({
            machine: resolvedFault.machineId,
            faultCode: resolvedFault.faultCode,
          });
        }
      }),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => {
        this.reportError(error);
        return EMPTY;
      })
    );
  }

  setState(partial: FaultRecommendationsStateModel): void {
    this.patch(partial);
  }

  resetState(): void {
    this.patch({
      fault: undefined,
      page: undefined,
      pageSize: undefined,
      withoutRecommendations: undefined,
    });
  }

  private patch(partial: FaultRecommendationsStateModel): void {
    this.state.update((state) => ({ ...state, ...partial }));
  }

  private reportError(error: ApiErrorResponse): void {
    if (error) {
      this.errorHandler.handleError(error);
    }
  }
}
